import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FileText, History, Loader2, RefreshCw, Shield, Trash2, Upload } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import insuranceService from '../../services/equipmentInsurance';
import type { InsurancePolicy } from '../../types/equipmentInsurance';
import { COVER_TYPE_LABELS, TONE_CLASSES, insuranceState } from '../../utils/equipmentInsurance';
import { money } from '../../utils/nurseryMoney';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { cn } from '../../utils/cn';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-4 gap-y-0.5 py-1.5 border-b border-neutral-100 last:border-0">
      <dt className="text-sm text-neutral-500">{label}</dt>
      <dd className="text-sm text-neutral-800 text-right break-words min-w-0">{children}</dd>
    </div>
  );
}

/**
 * /equipment/insurance/:id — one policy: its machines, its schedule, the
 * policies before and after it. A renewed or cancelled policy is shown as it
 * was and offers nothing to change.
 */
export default function InsurancePolicyDetail() {
  const { id } = useParams<{ id: string }>();
  const policyId = Number(id);
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { hasAccess: canManage } = useModuleLicense('vehicles', ['creator', 'admin']);
  const [policy, setPolicy] = useState<InsurancePolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setError(null);
    insuranceService.get(policyId)
      .then(res => {
        if (res.success && res.data) setPolicy(res.data);
        else setError('Policy not found.');
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Could not load the policy.'));
  }, [policyId]);

  useEffect(() => { load(); }, [load]);

  async function run(action: () => Promise<{ success: boolean; data?: InsurancePolicy }>) {
    setBusy(true);
    setActionError(null);
    try {
      const res = await action();
      if (res.success && res.data) setPolicy(res.data);
      return true;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'That did not save.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function confirmCancel() {
    if (!reason.trim()) { setActionError('Say why it is being cancelled.'); return; }
    if (await run(() => insuranceService.cancel(policyId, reason.trim()))) {
      setCancelling(false);
      setReason('');
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) await run(() => insuranceService.uploadDocument(policyId, file));
  }

  async function openSchedule() {
    if (!policy) return;
    try {
      await insuranceService.openDocument(policy);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not open the schedule.');
    }
  }

  const date = (d: string | null | undefined) => (d ? formatLocalDateInTz(d, orgTz) : '—');

  if (!policy) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title="Insurance policy" subtitle={error ? undefined : 'Loading…'} />
        <div className="max-w-3xl mx-auto px-4 py-6">
          {error ? <p className="text-sm text-red-600">{error}</p> : (
            <p className="text-sm text-neutral-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
          )}
        </div>
      </div>
    );
  }

  const st = insuranceState(policy);
  const isCurrent = policy.status === 'current';
  const manage = canManage && isCurrent;
  const items = policy.items ?? [];

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title={policy.insurer}
        subtitle={`Policy ${policy.policy_number}`}
        actionButtons={manage ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => navigate(`/equipment/insurance/${policy.id}/renew`)}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Renew
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/equipment/insurance/${policy.id}/edit`)}>
              Edit
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelling(true)}>
              Cancel policy
            </Button>
          </div>
        ) : undefined}
      />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', TONE_CLASSES[st.tone])}>{st.label}</span>
          {policy.status === 'renewed' && policy.renewed_by_id && (
            <Link to={`/equipment/insurance/${policy.renewed_by_id}`} className="text-sm text-primary-600 hover:underline">
              Open the policy that replaced it
            </Link>
          )}
        </div>

        {st.tone === 'expired' && (
          <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            This policy expired on {date(policy.expiry_date)}. Its machines are not covered until it is renewed.
            {manage && ' Once the insurer confirms the renewal, press Renew and enter the new expiry.'}
          </p>
        )}
        {policy.status === 'cancelled' && (
          <p className="text-sm text-neutral-700 bg-neutral-100 rounded-xl px-4 py-3 break-words">
            Cancelled{policy.cancelled_reason ? `: ${policy.cancelled_reason}` : '.'}
          </p>
        )}

        {cancelling && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              <label htmlFor="cancel-reason" className="block text-sm font-medium text-neutral-700">
                Why is it being cancelled?
              </label>
              <input
                id="cancel-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Sold the ute, or entered by mistake"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
              <p className="text-xs text-neutral-500">
                The policy is kept, marked cancelled. Its machines will show as having no insurance.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="danger" isLoading={busy} onClick={confirmCancel}>Cancel policy</Button>
                <Button size="sm" variant="ghost" onClick={() => { setCancelling(false); setActionError(null); }}>Keep it</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {actionError && <p className="text-sm text-red-600">{actionError}</p>}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-neutral-500" /> Cover
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl>
              <Row label="Cover">{COVER_TYPE_LABELS[policy.cover_type] ?? policy.cover_type}</Row>
              <Row label="Starts">{date(policy.start_date)}</Row>
              <Row label="Expires">{date(policy.expiry_date)}</Row>
              {policy.sum_insured != null && <Row label="Sum insured">{money(policy.sum_insured)}</Row>}
              {policy.excess != null && <Row label="Excess">{money(policy.excess)}</Row>}
              {policy.premium != null && <Row label="Premium inc GST">{money(policy.premium)}</Row>}
              {policy.broker_name && <Row label="Broker">{policy.broker_name}</Row>}
              {policy.broker_phone && <Row label="Broker phone"><a href={`tel:${policy.broker_phone}`} className="text-primary-600">{policy.broker_phone}</a></Row>}
              {policy.broker_email && <Row label="Broker email"><a href={`mailto:${policy.broker_email}`} className="text-primary-600 break-all">{policy.broker_email}</a></Row>}
            </dl>
            {policy.notes && <p className="text-sm text-neutral-700 mt-3 whitespace-pre-wrap break-words">{policy.notes}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Machines on this policy ({items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <p className="text-sm text-neutral-500">No machines listed.{manage && ' Edit the policy to add them.'}</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {items.map(it => (
                  <li key={it.equipment_id} className="py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                    <div className="min-w-0">
                      <Link to={`/equipment/${it.equipment_id}`} className="text-sm font-medium text-neutral-800 hover:underline break-words">
                        {it.equipment_name}
                      </Link>
                      <p className="text-xs text-neutral-500 break-words">
                        {[it.make, it.model, it.year_of_manufacture, it.rego_number].filter(Boolean).join(' · ') || it.equipment_type}
                        {it.equipment_status === 'retired' && ' · retired'}
                      </p>
                    </div>
                    {it.agreed_value != null && (
                      <span className="text-sm text-neutral-700 flex-shrink-0">{money(it.agreed_value)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {canManage && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-neutral-500" /> Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {policy.has_document ? (
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={openSchedule} className="text-sm text-primary-600 hover:underline break-all text-left">
                    {policy.document_name || 'Open the schedule'}
                  </button>
                  {manage && (
                    <Button size="sm" variant="ghost" isLoading={busy} onClick={() => run(() => insuranceService.removeDocument(policyId))}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No schedule attached.</p>
              )}
              {manage && (
                <>
                  <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} />
                  <Button size="sm" variant="outline" isLoading={busy} onClick={() => fileRef.current?.click()}>
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    {policy.has_document ? 'Replace' : 'Attach'} the schedule or certificate
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {(policy.history ?? []).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4 text-neutral-500" /> Other years of this policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {policy.history!.map(h => (
                  <li key={h.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-sm">
                    <Link to={`/equipment/insurance/${h.id}`} className="text-neutral-700 hover:underline break-all min-w-0">
                      {h.insurer} {h.policy_number}
                    </Link>
                    <span className="text-xs text-neutral-500">
                      {date(h.start_date)} – {date(h.expiry_date)} · {h.status === 'current' ? 'Current' : h.status === 'renewed' ? 'Renewed' : 'Cancelled'}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
